/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { AnvilorFormsAppStore } from "../anvilor-forms-app-store.service";
import { AnvilorFormsAppStoreContext } from "../anvilor-forms-app-store.service";
import { AnvilorFormsApiService } from "../../services/anvilor-forms-api.service";
import { QueryParamService } from "@anvilor/ngx-apps-core";
import { AnvilorNotificationsService } from "@anvilor/ngx-apps-core";
import { tap } from "rxjs/operators";
import { catchError } from "rxjs";
import { EMPTY } from "rxjs";
import { Observable } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { ANVILOR_FORM_NOT_FOUND_ERROR_MESSAGE } from "../../data/error-messages";
import { EXIT_APP_REDIRECT_URL } from "../../consts";
import { strip } from "@anvilor/ngx-core-utils";
import { AnvilorFormSubmissionUXConfig } from "../../models/anvilor-form-config";

export class AnvilorFormDocumentLoader {
  private formsApi: AnvilorFormsApiService;
  private qparams: QueryParamService;
  private store: AnvilorFormsAppStore;
  private notify: AnvilorNotificationsService;

  constructor(ctx: AnvilorFormsAppStoreContext) {
    this.formsApi = ctx.formsApi;
    this.qparams = ctx.qparams;
    this.store = ctx.store;
    this.notify = ctx.notify;
  }

  /**
   * We get the config from the current path
   */
  loadSubmissionUxConfig(): Observable<AnvilorFormSubmissionUXConfig> {
    this.checkRootUrl();
    return this.formsApi.getAnvilorFormConfigAtCurrentPath().pipe(
      tap((config) => this.handleRetrievedConfig(config)),
      catchError((e) => this.onLoadError(e))
    );
  }

  private handleRetrievedConfig(
    anvilor_form_config: AnvilorFormSubmissionUXConfig
  ): void {
    this.store.patchState({
      anvilor_form_config_load_status: "complete",
      anvilor_form_config,
    });
  }

  private onLoadError(e: HttpErrorResponse): Observable<never> {
    console.error(e);
    this.notify.error.active(
      {
        error_code: "ANVILOR_FORM_NOT_FOUND",
        title: "URL Not Found",
        body: ANVILOR_FORM_NOT_FOUND_ERROR_MESSAGE,
      },
      {
        buttonLabel: "Exit",
        click: () => window.location.assign(EXIT_APP_REDIRECT_URL),
      }
    );
    this.store.patchState({
      rui_initialization_status: "complete",
      edit_config_load_status: "complete",
      response_data_load_status: "complete",
      anvilor_form_config_load_status: "complete",
      auth_load_status: "complete",
    });
    return EMPTY;
  }

  private checkRootUrl() {
    // If no path, have to exit app since no config...
    if (strip(window.location.pathname, "/") === "") {
      window.location.assign(EXIT_APP_REDIRECT_URL);
    }

    // Maintain compatibility with legacy links to surveys*
    if (window.location.href.startsWith("https://surveys")) {
      const newUrl = window.location.href.replace("https://surveys", "https://forms");
      window.location.assign(newUrl);
    }
  }
}
